import { Progress, Table } from "flowbite-react";
import { BsStars } from "react-icons/bs";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { useEmptyStateTracking } from "../../hooks/useEmptyStateTracking";

// Using actual hex colors for both Progress bar (color names) and PieChart (hex)
const COLORS = [
  { name: "indigo", hex: "#6366f1" },
  { name: "red", hex: "#ef4444" },
  { name: "teal", hex: "#14b8a6" },
];

const columns = ["Reason", "No of accounts", "Percentage"];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="rounded-md border border-gray-300 bg-white p-2 text-xs drop-shadow-2xl">
        <p>{`${data.reason}: ${data.accounts_count}`}</p>
        <p>{`Accounts: ${data.accounts_count}`}</p>
        <p>{`Contracts: ${data.contracts_count}`}</p>
        <p>{`Percentage: ${data.percentage}%`}</p>
      </div>
    );
  }
  return null;
};

const SkeletonChart = () => (
  <div className="mx-5 mb-5 flex flex-row items-center justify-center gap-2.5 rounded-lg bg-white p-5 pb-4 shadow">
    <Table className="drop-shadow-none">
      <Table.Head>
        {columns.map((column) => (
          <Table.HeadCell key={column}>{column}</Table.HeadCell>
        ))}
      </Table.Head>
      <Table.Body>
        {Array.from({ length: 3 }).map((_, index) => (
          <Table.Row key={index} className="animate-pulse">
            <Table.Cell>
              <div className="h-4 w-24 rounded bg-gray-200"></div>
            </Table.Cell>
            <Table.Cell>
              <div className="h-4 w-12 rounded bg-gray-200"></div>
            </Table.Cell>
            <Table.Cell className="flex items-center gap-2">
              <div className="h-4 w-12 rounded bg-gray-200"></div>
              <div className="h-2 w-20 rounded bg-gray-200"></div>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
    <div className="flex h-[321px] w-[40%] items-center justify-center">
      <div className="h-[280px] w-[280px] animate-pulse rounded-full bg-gray-200"></div>
    </div>
  </div>
);

export const ChurnPageChart = ({ summary, isLoading }) => {
  const reasons = summary?.reasons || [];
  const total = summary?.total;

  // Hook must run unconditionally (before the loading early-return).
  useEmptyStateTracking("churn_chart", {
    isEmpty: reasons.length === 0,
    isLoading: !!isLoading,
  });

  if (isLoading) {
    return <SkeletonChart />;
  }

  if (reasons.length === 0) {
    return (
      <div className="mx-5 mb-5 rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-8 shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50">
            <BsStars className="text-2xl text-indigo-400" />
          </div>
          <div className="text-center">
            <p className="text-base font-medium text-gray-700">
              No churn data available
            </p>
            <p className="mt-1 text-sm text-gray-500">
              There are no churned accounts in this time period
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-5 mb-5 flex flex-row items-center justify-center gap-2.5 rounded-lg bg-white p-5 pb-4 shadow">
      <Table className="drop-shadow-none">
        <Table.Head>
          {columns.map((column) => (
            <Table.HeadCell key={column}>{column}</Table.HeadCell>
          ))}
        </Table.Head>

        <Table.Body>
          {reasons.map(({ reason, accounts_count, percentage }, index) => (
            <Table.Row key={reason}>
              <Table.Cell className="max-w-[450px]">{reason}</Table.Cell>
              <Table.Cell>{accounts_count}</Table.Cell>
              <Table.Cell className="w-50 flex items-center gap-2">
                {percentage}%
                <Progress
                  className="w-20"
                  progress={percentage}
                  size="sm"
                  color={COLORS[index % COLORS.length].name}
                />
              </Table.Cell>
            </Table.Row>
          ))}
          <Table.Row key="total" className="font-bold text-black">
            <Table.Cell>Total</Table.Cell>
            <Table.Cell>{total.accounts_count}</Table.Cell>
            <Table.Cell className="w-50 flex items-center gap-2">
              {total.percentage}%
              <Progress
                className="w-20"
                progress={total.percentage}
                size="sm"
                color="gray"
              />
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      <div className="h-[321px] w-[40%]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={reasons}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={140}
              fill="#8884d8"
              dataKey="percentage"
            >
              {reasons.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length].hex}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
